import { useSettingsStore } from '~/stores/settings';
import RequestService from './request';

interface GeneralSettingsRequest {
    ['branding.name']: string;
    ['misc.required_2fa']: number;
    ['misc.graphs_show_max_resource_limit']: boolean;
    ['misc.hide_admin_audit_logs_from_users']: boolean;
    ['default_locale']: string;
}

interface NodesSettingsRequest {
    ['deployment.strategy']: string;
    ['misc.databases_allow_random']: boolean;
}

interface EmbedSettingsRequest {
    ['og.site_name']: string;
    ['og.title']: string;
    ['og.description']: string;
    ['og.color']: string;
    ['og.image']: string;
}

interface JSSettingsRequest {
    js: string;
}

interface CSSSettingsRequest {
    css: string;
}

interface SSOSettingsRequest {
    ['whmcs.enabled']: boolean;
    ['whmcs.button_text']: string;
    ['whmcs.url']: string;
    ['whmcs.client_id']: string;
    ['whmcs.client_secret']: string;
    ['whmcs.api_id']: string;
    ['whmcs.api_secret']: string;
}

interface SMTPSettingsRequest {
    ['smtp.enabled']: boolean;
    ['smtp.host']: string;
    ['smtp.port']: number;
    ['smtp.encryption']: string;
    ['smtp.username']: string;
    ['smtp.password']: string;
    ['smtp.from_address']: string;
    ['smtp.from_name']: string;
}

interface FirewallProviderEntry {
    type: string;
    prefixes: string[];
    url?: string;
    api_key?: string;
}

interface FirewallSettingsRequest {
    providers: FirewallProviderEntry[];
}

interface AssetRequest {
    asset?: File;
}

class SettingsService {
    fetchAsset(url: string) {
        // Avoid using axios due to CORS
        return fetch(url).then((res) => res.text());
    }

    updateGeneral(data: GeneralSettingsRequest) {
        // TODO: check if lang should change
        return RequestService.post('/settings/general', data).then(() => useSettingsStore().updateSettings(data));
    }

    updateNodes(data: NodesSettingsRequest) {
        return RequestService.post('/settings/nodes', data).then(() => useSettingsStore().updateSettings(data as any));
    }

    updateEmbed(data: EmbedSettingsRequest) {
        return RequestService.post('/settings/embed', data).then(() => useSettingsStore().updateSettings(data as any));
    }

    updateJS(data: JSSettingsRequest) {
        return RequestService.post('/settings/js', data).then(({ url }) => useSettingsStore().updateSettings({ injector: { js: url } }));
    }

    updateCSS(data: CSSSettingsRequest) {
        return RequestService.post('/settings/css', data).then(({ url }) => useSettingsStore().updateSettings({ injector: { css: url } }));
    }

    updateSSO(data: SSOSettingsRequest) {
        return RequestService.post('/settings/sso', data).then(() => useSettingsStore().updateSettings(data as any));
    }

    updateSMTP(data: SMTPSettingsRequest) {
        return RequestService.post('/settings/smtp', data).then(() => useSettingsStore().updateSettings(data as any));
    }

    updateFirewall(data: FirewallSettingsRequest) {
        return RequestService.post('/settings/firewall', data).then(() => useSettingsStore().updateSettings({ firewall: data }));
    }

    private assetToFormData(data: AssetRequest) {
        const formData = new FormData();
        if (data.asset) formData.append('asset', data.asset);
        return formData;
    }

    uploadLoginLogo(data: AssetRequest) {
        return RequestService.post('/settings/branding/login_logo', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { login_logo: url },
            } as any),
        );
    }

    uploadFavicon(data: AssetRequest) {
        return RequestService.post('/settings/branding/favicon', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { favicon: url },
            } as any),
        );
    }

    uploadLogo(data: AssetRequest) {
        return RequestService.post('/settings/branding/logo', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { logo: url },
            } as any),
        );
    }
}

export default new SettingsService();
